import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, setStoredSignupIntent } from "../../context/AuthContext";
import BrandMark from "../../components/BrandMark";
import Seo from "../../components/Seo";

export default function SignupPage() {
  const {
    signUp,
    syncAccount,
    isAuthenticated,
    loading,
    isDevMode,
    emailVerified,
    isPartner,
    isAdmin,
    user,
  } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const asPartner = searchParams.get("as") === "partner";
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    if (asPartner) setStoredSignupIntent("partner");
  }, [asPartner]);

  const needsPartnerConversion =
    asPartner &&
    isAuthenticated &&
    !loading &&
    !isPartner &&
    !isAdmin &&
    user?.role !== "partner";

  // Authenticated instructor (or other non-partner) arriving with ?as=partner:
  // convert via sync instead of redirecting to /partner (which PartnerShell bounces).
  useEffect(() => {
    if (!needsPartnerConversion) return undefined;
    let cancelled = false;
    setConverting(true);
    setError("");
    // #region agent log
    fetch('http://127.0.0.1:7756/ingest/ceb2033a-b7c9-4b6e-98ec-7b086fdb35f1',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'6ffe9a'},body:JSON.stringify({sessionId:'6ffe9a',runId:'post-fix',hypothesisId:'B',location:'SignupPage.jsx:convert',message:'Converting authenticated user to partner',data:{role:user?.role||null},timestamp:Date.now()})}).catch(()=>{});
    // #endregion
    syncAccount(user?.display_name || undefined, { intent: "partner" })
      .then(() => {
        if (cancelled) return;
        // #region agent log
        fetch('http://127.0.0.1:7756/ingest/ceb2033a-b7c9-4b6e-98ec-7b086fdb35f1',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'6ffe9a'},body:JSON.stringify({sessionId:'6ffe9a',runId:'post-fix',hypothesisId:'B',location:'SignupPage.jsx:convert-ok',message:'Partner conversion succeeded',data:{},timestamp:Date.now()})}).catch(()=>{});
        // #endregion
      })
      .catch((err) => {
        if (cancelled) return;
        // #region agent log
        fetch('http://127.0.0.1:7756/ingest/ceb2033a-b7c9-4b6e-98ec-7b086fdb35f1',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'6ffe9a'},body:JSON.stringify({sessionId:'6ffe9a',runId:'post-fix',hypothesisId:'B',location:'SignupPage.jsx:convert-error',message:'Partner conversion failed',data:{error:String(err?.message||err)},timestamp:Date.now()})}).catch(()=>{});
        // #endregion
        setError(err.message || "Could not switch to a partner account");
      })
      .finally(() => {
        if (!cancelled) setConverting(false);
      });
    return () => {
      cancelled = true;
    };
  }, [needsPartnerConversion, syncAccount, user?.display_name, user?.role]);

  const postAuthPath = asPartner || isPartner || user?.role === "partner"
    ? "/partner/onboarding/basics"
    : "/instructor/onboarding/basics";
  const dashboardPath =
    asPartner || isPartner || user?.role === "partner" ? "/partner" : "/instructor";

  // #region agent log
  useEffect(() => {
    fetch('http://127.0.0.1:7756/ingest/ceb2033a-b7c9-4b6e-98ec-7b086fdb35f1',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'6ffe9a'},body:JSON.stringify({sessionId:'6ffe9a',runId:'post-fix',hypothesisId:'B',location:'SignupPage.jsx:mount',message:'Signup page state',data:{asPartner,search:window.location.search,loading,isAuthenticated,emailVerified,isPartner,role:user?.role||null,dashboardPath,needsPartnerConversion,converting},timestamp:Date.now()})}).catch(()=>{});
  }, [asPartner, loading, isAuthenticated, emailVerified, isPartner, user?.role, dashboardPath, needsPartnerConversion, converting]);
  // #endregion

  if (!loading && isAuthenticated) {
    if (!isDevMode && !emailVerified) {
      // #region agent log
      fetch('http://127.0.0.1:7756/ingest/ceb2033a-b7c9-4b6e-98ec-7b086fdb35f1',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'6ffe9a'},body:JSON.stringify({sessionId:'6ffe9a',runId:'post-fix',hypothesisId:'B',location:'SignupPage.jsx:auth-redirect',message:'Signup redirecting authenticated user',data:{asPartner,emailVerified,isDevMode,role:user?.role||null,isPartner,dest:'/verify-email'},timestamp:Date.now()})}).catch(()=>{});
      // #endregion
      return <Navigate to="/verify-email" replace />;
    }
    if (needsPartnerConversion || converting) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-emerald-50 to-white px-4">
          <div className="w-full max-w-md rounded-2xl border border-gray-100 bg-white p-8 text-center shadow-sm">
            <p className="text-sm text-gray-600">
              {error
                ? error
                : "Switching your account to partner onboarding…"}
            </p>
            {error && (
              <Link
                to="/become-a-partner"
                className="mt-4 inline-block text-sm font-medium text-emerald-700 underline"
              >
                Back to become a partner
              </Link>
            )}
          </div>
        </div>
      );
    }
    // #region agent log
    fetch('http://127.0.0.1:7756/ingest/ceb2033a-b7c9-4b6e-98ec-7b086fdb35f1',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'6ffe9a'},body:JSON.stringify({sessionId:'6ffe9a',runId:'post-fix',hypothesisId:'B',location:'SignupPage.jsx:auth-redirect',message:'Signup redirecting authenticated user',data:{asPartner,emailVerified,isDevMode,role:user?.role||null,isPartner,dest:dashboardPath},timestamp:Date.now()})}).catch(()=>{});
    // #endregion
    return <Navigate to={dashboardPath} replace />;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const options = asPartner ? { intent: "partner" } : {};
      // #region agent log
      fetch('http://127.0.0.1:7756/ingest/ceb2033a-b7c9-4b6e-98ec-7b086fdb35f1',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'6ffe9a'},body:JSON.stringify({sessionId:'6ffe9a',runId:'post-fix',hypothesisId:'D',location:'SignupPage.jsx:submit',message:'Partner/instructor signup submit',data:{asPartner,intent:options.intent||null},timestamp:Date.now()})}).catch(()=>{});
      // #endregion
      await signUp(email.trim(), password, displayName.trim(), options);
      if (isDevMode) {
        navigate(postAuthPath, { replace: true });
      } else {
        navigate("/verify-email", { replace: true });
      }
    } catch (err) {
      // #region agent log
      fetch('http://127.0.0.1:7756/ingest/ceb2033a-b7c9-4b6e-98ec-7b086fdb35f1',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'6ffe9a'},body:JSON.stringify({sessionId:'6ffe9a',runId:'post-fix',hypothesisId:'D',location:'SignupPage.jsx:submit-error',message:'Signup failed',data:{asPartner,error:String(err?.message||err)},timestamp:Date.now()})}).catch(()=>{});
      // #endregion
      setError(err.message || "Sign up failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-emerald-50 to-white px-4 py-12">
      <Seo
        title={
          asPartner
            ? "Partner Sign Up | Yoga School / Retreat Account"
            : "Instructor Sign Up | Create Yoga Teacher Account"
        }
        description={
          asPartner
            ? "Create your Yoga Retreats Booking partner account for yoga schools and retreat providers in India."
            : "Create your Yoga Retreats Booking instructor account. Sign up to build a verified online or prenatal yoga teacher profile and publish classes for students."
        }
        canonicalPath={asPartner ? "/signup?as=partner" : "/signup"}
      />
      <div className="w-full max-w-md">
        <Link to="/" className="mb-8 flex items-center justify-center gap-2">
          <BrandMark />
          <span className="font-bold text-gray-900">
            Yoga Retreats <span className="text-emerald-600">Booking</span>
          </span>
        </Link>
        <div className="rounded-2xl border border-gray-100 bg-white p-8 shadow-sm">
          <h1 className="mb-1 text-2xl font-semibold text-gray-900">
            {asPartner ? "Create your partner account" : "Create your instructor account"}
          </h1>
          <p className="mb-6 text-sm text-gray-500">
            {asPartner
              ? "Join as a yoga school or retreat provider. You’ll share org details and your website — our team lists programs after review."
              : "Join as an online or prenatal yoga teacher."}
            {isDevMode
              ? " (dev auth mode — email verification skipped)"
              : " We’ll send a verification email so you can confirm your account before onboarding."}
          </p>
          <p className="mb-6 text-sm text-emerald-800">
            New here?{" "}
            <Link
              to={asPartner ? "/become-a-partner" : "/become-an-instructor"}
              className="font-medium underline"
            >
              {asPartner
                ? "Learn how partner onboarding works"
                : "Learn how instructor onboarding works"}
            </Link>
            .
          </p>
          {error && (
            <div className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {asPartner ? "Your name" : "Full name"}
              </span>
              <input
                required
                minLength={2}
                name="name"
                autoComplete="name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Email</span>
              <input
                type="email"
                required
                name="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Password</span>
              <input
                type="password"
                required={!isDevMode}
                minLength={isDevMode ? 0 : 6}
                name="new-password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </label>
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-lg bg-emerald-600 py-2.5 font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              {submitting ? "Creating account…" : "Create account"}
            </button>
          </form>
          <p className="mt-6 text-center text-sm text-gray-600">
            Already have an account?{" "}
            <Link
              to={asPartner ? "/login?as=partner" : "/login"}
              className="font-medium text-emerald-700 hover:underline"
            >
              Sign in
            </Link>
          </p>
          {!asPartner && (
            <p className="mt-3 text-center text-xs text-gray-500">
              Yoga school or retreat?{" "}
              <Link to="/signup?as=partner" className="text-emerald-700 underline">
                Sign up as a partner
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
